"use client";

import { useMemo, useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FiBookOpen, FiFilm, FiMusic, FiPackage, FiSmile, FiMessageCircle } from "react-icons/fi";
import {
  type GetPreparedJson,
  type GetPreparedKey,
  parseMarkdownTable,
  sanitizeGetPreparedJson,
  splitListCell,
  splitMdAtSources,
} from "@/lib/guides/getPrepared";
import { getGuideUiStrings } from "@/lib/guides/uiLocale";

function labelForKey(k: GetPreparedKey, languageCode?: string | null): string {
  const ui = getGuideUiStrings(languageCode);
  if (k === "clothes") return ui.preparedClothes;
  if (k === "manners") return ui.preparedManners;
  if (k === "vocabulary") return ui.preparedVocabulary;
  if (k === "books") return ui.preparedBooks;
  if (k === "music") return ui.preparedMusic;
  return ui.preparedCinema;
}

function iconForKey(k: GetPreparedKey) {
  if (k === "clothes") return FiPackage;
  if (k === "manners") return FiSmile;
  if (k === "vocabulary") return FiMessageCircle;
  if (k === "books") return FiBookOpen;
  if (k === "music") return FiMusic;
  return FiFilm;
}

function stripInlineMarkdown(text: string): string {
  return String(text || "")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/__(.*?)__/g, "$1")
    .replace(/_(.*?)_/g, "$1")
    .replace(/`(.*?)`/g, "$1")
    .trim();
}

function normalizePreparedCategoryKey(raw: string): "essentials" | "seasonal" | "respectful" | "optional" | null {
  const key = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z\u00c0-\u024f\u3040-\u30ff\u3400-\u9fff]+/g, "");
  if (!key) return null;
  const matches = (list: string[]) => list.includes(key);
  if (matches(["essentials", "essentiels", "essenzials", "essenziali", "esenciales", "essenciais", "essentiels", "basis", "grundlegend", "基本"])) {
    return "essentials";
  }
  if (matches(["seasonal", "saisonal", "stagionali", "estacionales", "sazonais", "saisonnier", "saeson", "säsong", "季節"])) {
    return "seasonal";
  }
  if (matches(["respectful", "respectueux", "rispettoso", "respetuoso", "respeitoso", "respectvol", "respektfullt", "配慮"])) {
    return "respectful";
  }
  if (matches(["optional", "facultatif", "opzionale", "opcional", "vrijblijvend", "valfritt", "任意"])) {
    return "optional";
  }
  return null;
}

function localizedPreparedCategoryLabel(raw: string, ui: ReturnType<typeof getGuideUiStrings>): string {
  const normalized = normalizePreparedCategoryKey(raw);
  if (normalized === "essentials") return ui.tabEssentials;
  if (normalized === "seasonal") return ui.tabSeasonal;
  if (normalized === "respectful") {
    return {
      en: "Respectful",
      da: "Respektfuldt",
      fr: "Respectueux",
      de: "Respektvoll",
      it: "Rispettoso",
      es: "Respetuoso",
      pt: "Respeitoso",
      nl: "Respectvol",
      sv: "Respektfullt",
      ja: "配慮しておきたいこと",
    }[ui.locale.slice(0, 2)] || "Respectful";
  }
  if (normalized === "optional") {
    return {
      en: "Optional",
      da: "Valgfrit",
      fr: "Facultatif",
      de: "Optional",
      it: "Opzionale",
      es: "Opcional",
      pt: "Opcional",
      nl: "Optioneel",
      sv: "Valfritt",
      ja: "あると便利",
    }[ui.locale.slice(0, 2)] || "Optional";
  }
  return raw;
}

type Props = {
  value: GetPreparedJson | null | undefined;
  title?: string;
  languageCode?: string | null;
  activeKey?: GetPreparedKey;
  onActiveKeyChange?: (key: GetPreparedKey) => void;
  showAllTabs?: boolean;
  keys?: GetPreparedKey[];
};

export function GetPreparedSection({ value, title, languageCode, activeKey, onActiveKeyChange, showAllTabs, keys }: Props) {
  const prepared = useMemo(() => sanitizeGetPreparedJson(value), [value]);
  const ui = useMemo(() => getGuideUiStrings(languageCode), [languageCode]);

  const available = useMemo(() => {
    const candidateKeys: GetPreparedKey[] = keys?.length ? keys : ["clothes", "manners", "vocabulary", "books", "music", "cinema"];
    if (showAllTabs) return candidateKeys;
    return candidateKeys.filter((k) => {
      const md =
        k === "clothes"
          ? prepared.clothes_md
          : k === "manners"
            ? prepared.manners_md
            : k === "vocabulary"
              ? prepared.vocabulary_md
              : k === "books"
                ? prepared.books_md
                : k === "music"
                  ? prepared.music_md
                  : prepared.cinema_md;
      return Boolean(String(md || "").trim());
    });
  }, [keys, prepared, showAllTabs]);

  const [internalActive, setInternalActive] = useState<GetPreparedKey>("clothes");
  const active = (activeKey || internalActive) as GetPreparedKey;
  useEffect(() => {
    const next = available[0] || null;
    if (!next) return;
    if (available.includes(active)) return;
    if (activeKey) onActiveKeyChange?.(next);
    else setInternalActive(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [available]);

  const activeMd = useMemo(() => {
    if (active === "clothes") return prepared.clothes_md || "";
    if (active === "manners") return prepared.manners_md || "";
    if (active === "vocabulary") return prepared.vocabulary_md || "";
    if (active === "books") return prepared.books_md || "";
    if (active === "music") return prepared.music_md || "";
    return prepared.cinema_md || "";
  }, [active, prepared]);

  const { main: activeMainMd, sources: activeSources } = useMemo(() => splitMdAtSources(activeMd), [activeMd]);

  const parsed = useMemo(() => parseMarkdownTable(activeMainMd), [activeMainMd]);

  const hasAny = available.length > 0;
  if (!hasAny) return null;

  return (
    <div className="mb-10">
      <p className="mb-2 font-semibold">{ui.getPreparedTitle}</p>
      {title ? <p className="text-xs text-slate-600">{ui.getPreparedSubtitle}</p> : null}

      <div className="not-prose mt-4">
        <div className="flex w-full max-w-full min-w-0 items-end gap-5 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:h-0 [&::-webkit-scrollbar]:w-0">
          {available.map((k) => {
            const isActive = k === active;
            const label = labelForKey(k, languageCode);
            return (
              <button
                key={k}
                type="button"
                onClick={() => {
                  if (activeKey) onActiveKeyChange?.(k);
                  else setInternalActive(k);
                }}
                aria-pressed={isActive}
                className={
                  "relative pb-2 text-sm font-semibold transition " +
                  (isActive ? "text-slate-900" : "text-slate-500 hover:text-slate-700")
                }
              >
                <span className="whitespace-nowrap">{label}</span>
                {isActive ? <span className="absolute left-0 right-0 -bottom-[1px] h-0.5 rounded-full bg-amber-300" /> : null}
              </button>
            );
          })}
        </div>
        <div className="mt-1 h-px w-full bg-slate-200" />
      </div>

      <div className="mt-4">
        {!String(activeMainMd || "").trim() ? (
          <div className="not-prose rounded-2xl border border-slate-200 bg-white/70 p-4 text-sm text-slate-600">
            {ui.getPreparedEmpty}
          </div>
        ) : active === "clothes" ? (
          <PreparedClothes headers={parsed.headers} rows={parsed.rows} languageCode={languageCode} />
        ) : active === "manners" ? (
          <PreparedManners headers={parsed.headers} rows={parsed.rows} languageCode={languageCode} />
        ) : active === "vocabulary" ? (
          <PreparedVocabulary headers={parsed.headers} rows={parsed.rows} />
        ) : (
          <PreparedMedia kind={active} headers={parsed.headers} rows={parsed.rows} />
        )}
      </div>

      {activeSources.length ? (
        <div className="not-prose mt-4 rounded-2xl border border-slate-200 bg-white/70 p-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">{ui.sources}</p>
          <ul className="mt-2 space-y-1 text-xs text-slate-700">
            {activeSources.map((s) => (
              <li key={s.uri} className="break-words">
                <a href={s.uri} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
                  {s.title}
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function PreparedClothes({
  headers,
  rows,
  languageCode,
}: {
  headers: string[];
  rows: string[][];
  languageCode?: string | null;
}) {
  const ui = getGuideUiStrings(languageCode);
  // Expect: | Category | Bring |
  const idxCategory = headers.findIndex((h) => h.toLowerCase() === "category");
  const idxBring = headers.findIndex((h) => h.toLowerCase() === "bring");
  const cats = new Map<string, string[]>();

  for (const r of rows) {
    const category = String(r[idxCategory >= 0 ? idxCategory : 0] || "").trim() || ui.tabEssentials;
    const bring = String(r[idxBring >= 0 ? idxBring : 1] || "").trim();
    const items = splitListCell(bring, 10);
    if (!items.length) continue;
    const normalized = normalizePreparedCategoryKey(category);
    const key = normalized || category.toLowerCase();
    const label = localizedPreparedCategoryLabel(category[0] ? category[0]!.toUpperCase() + category.slice(1) : category, ui);
    const prev = cats.get(key) || [];
    cats.set(key, [...prev, ...items].slice(0, 10));
    // Keep a stable label for display.
    cats.set(`__label__:${key}`, [label]);
  }

  const order = ["essentials", "seasonal", "respectful", "optional"];
  const blocks = order
    .map((k) => {
      const items = cats.get(k) || [];
      const label = (cats.get(`__label__:${k}`) || [k])[0]!;
      return { key: k, label, items };
    })
    .filter((b) => b.items.length > 0);

  if (!blocks.length) {
    return (
      <div className="prose prose-sm max-w-none prose-p:text-slate-700">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{rows.map((r) => r.join(" — ")).join("\n")}</ReactMarkdown>
      </div>
    );
  }

  return (
    <div className="not-prose grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {blocks.map((b) => (
        <div key={b.key} className="rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm">
          <p className="text-sm font-semibold text-slate-900">{b.label}</p>
          <ul className="mt-2 space-y-1 text-sm text-slate-700">
            {b.items.map((it, idx) => (
              <li key={idx} className="flex gap-2">
                <span className="mt-[7px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-slate-900" />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function PreparedManners({
  headers,
  rows,
  languageCode,
}: {
  headers: string[];
  rows: string[][];
  languageCode?: string | null;
}) {
  const ui = getGuideUiStrings(languageCode);
  const idxSituation = headers.findIndex((h) => h.toLowerCase() === "situation");
  const idxInstead = headers.findIndex((h) => h.toLowerCase() === "instead");
  const items = rows
    .map((r) => ({
      situation: String(r[idxSituation >= 0 ? idxSituation : 0] || "").trim(),
      instead: String(r[idxInstead >= 0 ? idxInstead : 1] || "").trim(),
    }))
    .filter((x) => x.situation || x.instead)
    .slice(0, 10);

  if (!items.length) return null;

  return (
    <div className="not-prose">
      <div className="hidden sm:grid grid-cols-2 gap-3 pb-1">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">{ui.preparedSituation}</p>
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">{ui.instead}</p>
      </div>
      <div className="space-y-3">
        {items.map((r, idx) => (
          <div key={idx} className="rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm">
            <div className="grid gap-2 sm:grid-cols-2 sm:gap-3">
              <div>
                <p className="sm:hidden text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">{ui.preparedSituation}</p>
                <div className="mt-1 text-sm font-semibold text-slate-900">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ p: ({ children }) => <p className="m-0">{children}</p> }}>
                    {r.situation}
                  </ReactMarkdown>
                </div>
              </div>
              <div>
                <p className="sm:hidden text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">{ui.instead}</p>
                <div className="mt-1 text-sm text-slate-700">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      p: ({ children }) => <p className="m-0">{children}</p>,
                      a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2" />,
                    }}
                  >
                    {r.instead}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PreparedVocabulary({ headers, rows }: { headers: string[]; rows: string[][] }) {
  const idxPhrase = headers.findIndex((h) => h.toLowerCase() === "phrase");
  const idxMeaning = headers.findIndex((h) => h.toLowerCase() === "meaning");
  const idxWhen = headers.findIndex((h) => h.toLowerCase() === "when");
  const items = rows
    .map((r) => ({
      phrase: String(r[idxPhrase >= 0 ? idxPhrase : 0] || "").trim(),
      meaning: String(r[idxMeaning >= 0 ? idxMeaning : 1] || "").trim(),
      when: idxWhen >= 0 ? String(r[idxWhen] || "").trim() : "",
    }))
    .filter((x) => x.phrase || x.meaning)
    .slice(0, 16);

  if (!items.length) return null;

  return (
    <>
      <div className="md:hidden -mx-4 px-4 overflow-x-hidden">
        <div className="flex w-full max-w-full snap-x snap-mandatory gap-3 overflow-x-auto pb-2">
          {items.map((it, idx) => (
            <div key={idx} className="min-w-[240px] snap-start rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-sm font-semibold text-slate-900">{it.phrase || "—"}</p>
              <p className="mt-1 text-sm text-slate-700">{it.meaning}</p>
              {it.when ? <p className="mt-2 text-xs text-slate-600">{it.when}</p> : null}
            </div>
          ))}
        </div>
      </div>
      <div className="hidden md:grid grid-cols-2 gap-3 lg:grid-cols-3">
        {items.map((it, idx) => (
          <div key={idx} className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-sm font-semibold text-slate-900">{it.phrase || "—"}</p>
            <p className="mt-1 text-sm text-slate-700">{it.meaning}</p>
            {it.when ? <p className="mt-2 text-xs text-slate-600">{it.when}</p> : null}
          </div>
        ))}
      </div>
    </>
  );
}

function PreparedMedia({ kind, headers, rows }: { kind: GetPreparedKey; headers: string[]; rows: string[][] }) {
  const Icon = iconForKey(kind);
  const idxTitle = headers.findIndex((h) => h.toLowerCase() === "title");
  const idxWhy = headers.findIndex((h) => h.toLowerCase() === "why");
  const items = rows
    .map((r) => ({
      title: stripInlineMarkdown(String(r[idxTitle >= 0 ? idxTitle : 0] || "")),
      why: String(r[idxWhy >= 0 ? idxWhy : 1] || "").trim(),
    }))
    .filter((x) => x.title || x.why)
    .slice(0, 10);

  if (!items.length) return null;

  return (
    <div className="not-prose grid gap-3 sm:grid-cols-2">
      {items.map((it, idx) => (
        <div key={idx} className="rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-900/5 text-slate-800">
              <Icon size={18} />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900">{it.title || "—"}</p>
              {it.why ? (
                <div className="mt-1 text-sm text-slate-700">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      p: ({ children }) => <p className="m-0">{children}</p>,
                      a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2" />,
                    }}
                  >
                    {it.why}
                  </ReactMarkdown>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
